import React from "react";
import { usePage } from "@inertiajs/react";

export default function Breadcrumbs() {
    const { url, props } = usePage();

    // Utility to clean URL
    const cleanUrlPath = url
        .split("?")[0]; // Remove query params

    // Don't show breadcrumbs on homepage
    if (cleanUrlPath === "/" || cleanUrlPath === "") {
        return null;
    }

    // Generate breadcrumbs data
    const generateBreadcrumbs = () => {
        const pathSegments = cleanUrlPath
            .split("/")
            .filter((segment) => segment !== "");

        const baseUrl = typeof window !== "undefined" ? window.location.origin : "https://wizfreelance.com";

        // Custom names map (English slugs + Arabic slugs)
        const routeNames = {
            // English slugs (legacy redirects)
            services: "الخدمات",
            client: "العملاء",
            freelancer: "المستقلين",
            freelancers: "المستقلين",
            register: "التسجيل",
            "web-apps": "تصميم موقع الكتروني",
            "data-entry-management": "إدارة المتاجر الالكترونية",
            "mobile-app-development": "تصميم تطبيقات الجوال",
            "social-media-management": "إدارة حسابات التواصل الاجتماعي",
            "ads-management": "إدارة حملات إعلانية",
            seo: "شركة سيو",
            "graphic-design": "تصميم جرافيك",
            "motion-graphics": "تصميم موشن جرافيك",
            "voice-over": "فويس اوفر",
            "influencer-marketing": "التسويق عبر المؤثرين",
            "store-establishment": "تأسيس متاجر",
            // Arabic slugs (canonical)
            "تصميم-موقع-الكتروني": "تصميم موقع الكتروني",
            "ادارة-المتاجر-الالكترونية": "إدارة المتاجر الالكترونية",
            "تصميم-تطبيقات": "تصميم تطبيقات الجوال",
            "ادارة-حسابات-التواصل-الاجتماعي": "إدارة حسابات التواصل الاجتماعي",
            "ادارة-حملات-اعلانية": "إدارة حملات إعلانية",
            "شركة-سيو": "شركة سيو",
            "تصميم-جرافيك": "تصميم جرافيك",
            "تصميم-موشن-جرافيك": "تصميم موشن جرافيك",
            "فويس-اوفر": "فويس اوفر",
            "التسويق-عبر-المؤثرين": "التسويق عبر المؤثرين",
            "تأسيس-متاجر": "تأسيس المتاجر",
            // Other pages
            blogs: "المدونة",
            faq: "مركز المعرفة والأسئلة الشائعة",
            portfolio: "معرض الأعمال",
            profile: "البروفايل",
            about: "عن الشركة",
            contact: "تواصل معنا",
        };

        // Case studies aren't grouped under a real "/case-studies" listing
        // page — they're categorized by service instead. So this route gets
        // a custom 3-level trail (Home → service → case study name) from the
        // `work` prop CaseStudyController::show() already provides, rather
        // than the generic per-segment walk below (which would otherwise
        // show the literal untranslated segment "case-studies" and the raw
        // numeric id as the case study's "name").
        // Blog articles get a 4-level trail (Home → المدونة → Category →
        // Article) instead of the generic 3-level per-segment walk below,
        // since "category" is a data attribute on the blog, not a URL
        // segment the generic walk would ever see.
        if (pathSegments[0] === "blogs" && pathSegments.length > 1 && props?.blog) {
            const breadcrumbs = [
                { name: "الرئيسية", url: "/", isLast: false },
                { name: "المدونة", url: "/blogs", isLast: false },
            ];
            if (props.blog.category) {
                breadcrumbs.push({
                    name: props.blog.category,
                    url: `/blogs?category=${encodeURIComponent(props.blog.category)}`,
                    isLast: false,
                });
            }
            breadcrumbs.push({
                name: props.blog.title || "مقال",
                url: cleanUrlPath,
                isLast: true,
            });
            return breadcrumbs;
        }

        // /portfolio/work/{id} (identities, SEO works, company profiles)
        // renders the same profile page: name the trail after the work
        // instead of the raw "Work › 63" segments.
        if (pathSegments[0] === "portfolio" && pathSegments[1] === "work" && props?.work) {
            return [
                { name: "الرئيسية", url: "/", isLast: false },
                { name: "معرض الأعمال", url: "/portfolio", isLast: false },
                { name: props.work.name || "عمل", url: cleanUrlPath, isLast: true },
            ];
        }

        if (pathSegments[0] === "case-studies" && props?.work) {
            const breadcrumbs = [{ name: "الرئيسية", url: "/", isLast: false }];
            if (props.work.service_slug && props.work.service_label) {
                breadcrumbs.push({
                    name: props.work.service_label,
                    url: `/services/${props.work.service_slug}`,
                    isLast: false,
                });
            }
            breadcrumbs.push({
                name: props.work.name || "دراسة حالة",
                url: cleanUrlPath,
                isLast: true,
            });
            return breadcrumbs;
        }

        const breadcrumbs = [
            {
                name: "الرئيسية",
                url: "/",
                isLast: false,
            },
        ];

        let currentPath = "";

        pathSegments.forEach((segment, index) => {
            currentPath += `/${segment}`;

            // Get readable name
            let name = routeNames[segment];

            // If segment is a project ID and we have project data, use project name
            if (!name && props?.project && String(props.project.id) === segment && props.project.name) {
                name = props.project.name;
            }

            // If segment is a blog slug and we have blog data, use blog title
            if (!name && props?.blog && props.blog.title && (segment === props.blog.slug || (props.blog.seo_data && segment === props.blog.seo_data.slug))) {
                name = props.blog.title;
            }

            // Fallback: format the slug
            if (!name) {
                // Decode URI component to handle Arabic characters in URL
                try {
                    name = decodeURIComponent(segment);
                } catch (e) {
                    name = segment;
                }

                name = name
                    .replace(/-/g, " ")
                    // Capitalize first letter of words
                    .replace(/(?:^|\s)\S/g, function (a) {
                        return a.toUpperCase();
                    });
            }

            breadcrumbs.push({
                name: name,
                url: currentPath,
                isLast: index === pathSegments.length - 1,
            });
        });

        return breadcrumbs;
    };

    const breadcrumbs = generateBreadcrumbs();

    // JSON-LD Schema
    const origin = typeof window !== "undefined" ? window.location.origin : "https://wizfreelance.com";
    const schemaCleanup = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "@id": `${origin}${cleanUrlPath}#breadcrumbs`,
        itemListElement: breadcrumbs.map((crumb, index) => ({
            "@type": "ListItem",
            position: index + 1,
            name: crumb.name,
            item: `${origin}${crumb.url}`,
        })),
    };

    return (
        <div className="w-full relative z-40 bg-transparent pointer-events-none">
            {/* Schema Script */}
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify(schemaCleanup),
                }}
            />

            {/* No top padding here — WebsiteLayout already reserves the
                header-clearing offset above this component. Only the 20px
                gap to whatever content follows (page badge / H1) belongs
                here. */}
            <div className="wrapper mb-5 pointer-events-auto">
                <nav className="flex" aria-label="Breadcrumb">
                    <ol className="inline-flex items-center space-x-1 md:space-x-2 space-x-reverse">
                        {breadcrumbs.map((crumb, index) => (
                            <li
                                key={index}
                                className="inline-flex items-center"
                            >
                                {index > 0 && (
                                    <svg
                                        className="w-3 h-3 text-gray-400 mx-1 rtl:rotate-180"
                                        aria-hidden="true"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 6 10"
                                    >
                                        <path
                                            stroke="currentColor"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="m1 9 4-4-4-4"
                                        />
                                    </svg>
                                )}
                                <div className="flex items-center">
                                    {crumb.isLast ? (
                                        <span className="ms-1 text-xs md:text-sm font-medium text-gray-500 md:ms-2 truncate max-w-[200px] sm:max-w-xs cursor-default">
                                            {crumb.name}
                                        </span>
                                    ) : (
                                        <a
                                            href={crumb.url}
                                            className="inline-flex items-center text-xs md:text-sm font-medium text-gray-700 hover:text-blue-600  transition-colors duration-200"
                                        >
                                            {index === 0 && (
                                                <svg
                                                    className="w-3 h-3 me-2.5"
                                                    aria-hidden="true"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    fill="currentColor"
                                                    viewBox="0 0 20 20"
                                                >
                                                    <path d="m19.707 9.293-2-2-7-7a1 1 0 0 0-1.414 0l-7 7-2 2a1 1 0 0 0 1.414 1.414L2 10.414V18a2 2 0 0 0 2 2h3a1 1 0 0 0 1-1v-4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v4a1 1 0 0 0 1 1h3a2 2 0 0 0 2-2v-7.586l.293.293a1 1 0 0 0 1.414-1.414Z" />
                                                </svg>
                                            )}
                                            {crumb.name}
                                        </a>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ol>
                </nav>
            </div>
        </div>
    );
}
